import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { type FC, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useApiServiceCallback } from "../../../../server/api/client";
import { serviceURL } from "../../../../utils/service-url";
import { useAnalytics } from "../../../analytics/analytics";
import type { FollowUpMomentDetails } from "../../../follow-up/server/types";
import { MomentService } from "../../client";
import { useShowroomData } from "../../hooks/use-build-showroom-data";
import type { MomentFormMoment, MomentFormSchedule } from "../../types/moment-form-moment";
import { useFollowUpMomentDetails } from "../follow-up/hooks/use-follow-up-moment-details";
import { SegmentRecipientPreview } from "../segment-recipient-preview";
import { TimezoneWarning } from "../timezone-warning";

export const PublishDialog: FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
  setSaving: (saving: boolean) => void;
  schedule: MomentFormSchedule | null;
  onPublished: (details: FollowUpMomentDetails | null) => void;
}> = ({ schedule, open, setOpen, setSaving, onPublished }) => {
  const [publishing, setPublishing] = useState(false);
  const { data: showroomData } = useShowroomData();
  const { control, reset, getValues } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: ["timezone", "id", "showroom_key"] });
  const [timezone, momentId, immediate, showroom_key] = getValues([
    "timezone",
    "id",
    "immediate",
    "showroom_key",
  ]);

  const sharingUrl = `${serviceURL("showroom")}${showroom_key ?? ""}`;

  const { data: followUpMomentDetails, loading: followUpMomentDetailsLoading } =
    useFollowUpMomentDetails(momentId);

  const { gaEvent } = useAnalytics();

  const handleSubmit = useApiServiceCallback(
    MomentService,
    async ({ apiService }): Promise<void> => {
      setPublishing(true);

      const { error } = await apiService.setActiveStatus(
        momentId,
        true,
        true,
        showroomData?.body ?? undefined,
        showroomData?.subject ?? undefined,
      );
      if (error) {
        toast.error(error);
        setSaving(false);
        setPublishing(false);
        gaEvent("publish_error", { event_category: "moment", moment_id: momentId });
      } else {
        toast.success("Communication published!", { duration: 5000 });
        gaEvent("publish", { event_category: "moment", moment_id: momentId });
        setSaving(false);
        setPublishing(false);
        setOpen(false);
        reset((values) => ({
          ...values,
          running: true,
        }));
        onPublished(followUpMomentDetails ?? null);
      }
    },
    [momentId, gaEvent, immediate, followUpMomentDetails, onPublished, showroomData],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
        setSaving(false);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Publish Communication</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you wish to publish this Communication?
        </DialogContentText>
        {/* Recipient count is a <p> */}
        <Box sx={{ mt: 2 }}>
          <SegmentRecipientPreview show_cc_warning={true} />
        </Box>
        {timezone && momentId && (
          <Box>
            <TimezoneWarning momentId={momentId} />
          </Box>
        )}
        {schedule && (
          <DialogContentText>
            {/* <ScheduleInFutureWarning
              recipientTimezones={segmentCount.segmentRecipient.map((x) => x.timezone)}
              schedule={schedule}
              timezone={timezone}
            /> */}
          </DialogContentText>
        )}
        {showroomData && showroom_key && (
          <Alert
            severity="info"
            icon={false}
            action={
              <Button
                data-analytics-id="moment-form-publish-dialog-copy-link"
                color="inherit"
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(sharingUrl);
                  toast.success("Link copied to clipboard");
                }}
              >
                Copy Link
              </Button>
            }
            sx={{ mt: 2 }}
          >
            A public web version of the most recently published version of this Communication will
            be created. Anyone with the link can view it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="moment-form-publish-dialog-cancel"
          onClick={() => {
            setOpen(false);
            setSaving(false);
          }}
          disabled={publishing || followUpMomentDetailsLoading}
        >
          Cancel
        </Button>
        <Button
          data-analytics-id="moment-form-publish-dialog-confirm"
          loading={publishing}
          disabled={publishing || followUpMomentDetailsLoading}
          variant="contained"
          onClick={() => void handleSubmit().catch(console.error)}
        >
          Yes, I&apos;m sure
        </Button>
      </DialogActions>
    </Dialog>
  );
};
